import React, { useState } from 'react';
import DatabaseTest from '@/components/debug/DatabaseTest';
import GooglePlacesIntegrationTest from '@/components/debug/GooglePlacesIntegrationTest';

const IntegrationTestPage = () => {
  const [activeTab, setActiveTab] = useState('database');

  const tabs = [
    { id: 'database', name: 'Database Tests', component: DatabaseTest },
    { id: 'google-places', name: 'Google Places Integration', component: GooglePlacesIntegrationTest }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🔧 Integration Test Suite
          </h1>
          <p className="text-gray-600">
            Comprehensive testing for database connectivity and Google Places API integration
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-8 border-b border-gray-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-lg">
          {ActiveComponent && <ActiveComponent />}
        </div>

        {/* Test Status Overview */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              📊 Database Tests
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✅ Supabase connection</li>
              <li>✅ Table schema validation</li>
              <li>✅ CRUD operations</li>
              <li>✅ User authentication</li>
              <li>✅ Row Level Security</li>
            </ul>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              🗺️ Google Places Integration
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✅ API connectivity</li>
              <li>✅ Data transformation</li>
              <li>✅ Database storage</li>
              <li>✅ Error handling</li>
              <li>✅ Batch operations</li>
            </ul>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-blue-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">
            🚀 Quick Actions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Test Database</h4>
              <p className="text-sm text-gray-600 mb-3">
                Run comprehensive database connectivity and schema tests
              </p>
              <button
                onClick={() => setActiveTab('database')}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                Run Database Tests
              </button>
            </div>

            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Test Google Places</h4>
              <p className="text-sm text-gray-600 mb-3">
                Test Google Places API integration and database storage
              </p>
              <button
                onClick={() => setActiveTab('google-places')}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
              >
                Run Places Tests
              </button>
            </div>

            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">View Logs</h4>
              <p className="text-sm text-gray-600 mb-3">
                Check browser console for detailed test logs and debugging info
              </p>
              <button
                onClick={() => {
                  console.log('🔧 Integration Test Suite - Console Logs Available');
                  console.log('📊 Check the Network tab for API calls');
                  console.log('🗺️ Check the Console tab for detailed logs');
                }}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium"
              >
                Open Console
              </button>
            </div>
          </div>
        </div>

        {/* Troubleshooting Guide */}
        <div className="mt-8 bg-yellow-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-yellow-900 mb-4">
            🔍 Troubleshooting Guide
          </h3>
          <div className="text-sm text-yellow-800 space-y-2">
            <p><strong>If database tests fail:</strong> Check Supabase connection and environment variables</p>
            <p><strong>If Google Places tests fail:</strong> Verify API key and Places API is enabled</p>
            <p><strong>If transformation fails:</strong> Check data structure and validation logic</p>
            <p><strong>If saves fail:</strong> Verify database schema and field mappings</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationTestPage;
